// NotesManager.js - Version avec actualisation améliorée et liens cliquables

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { 
  Save, X, CheckCircle, AlertCircle, 
  ChevronDown, ChevronRight, Clock,
  Brain, HelpCircle, Target, Zap, Trophy, Sparkles,
  ChevronUp, Edit2, Package, ExternalLink
} from 'lucide-react';

// Configuration des 4 rideaux
const RIDEAU_CONFIG = {
  decisions: {
    id: 'decisions',
    title: 'Décisions & Actions',
    subtitle: 'Ce qui a été décidé et réalisé',
    icon: CheckCircle,
    color: 'green',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-700',
    placeholder: 'Documentez ici vos décisions prises, orientations choisies, actions réalisées et actions planifiées...'
  },
  questions: {
    id: 'questions',
    title: 'Questions & Défis',
    subtitle: 'Ce qui reste à résoudre et à accomplir',
    icon: HelpCircle,
    color: 'orange',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    textColor: 'text-orange-700',
    placeholder: 'Notez ici vos questions ouvertes, difficultés rencontrées, décisions à prendre, risques et opportunités, votre todo list et contacts à prendre...'
  },
  ai_recommendations: {
    id: 'ai_recommendations',
    title: 'Recommandations',
    subtitle: 'Conseils et suggestions personnalisées',
    icon: Brain,
    color: 'purple',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    textColor: 'text-purple-700',
    placeholder: 'Les recommandations d\'Aladinn, bonnes pratiques et ressources utiles apparaîtront ici...'
  },
  resources: {
    id: 'resources',
    title: 'Ressources & Résultats',
    subtitle: 'Documents, liens et résultats obtenus',
    icon: Package,
    color: 'blue',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-700',
    placeholder: 'Sauvegardez ici vos ressources utiles (liens, documents) et les résultats que vous avez obtenus...'
  }
};

// Composant pour afficher une entrée individuelle
const NoteEntry = ({ entry, index, onDelete }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const isFromChat = entry.source === 'chat';
  const hasCategory = entry.category && entry.category !== 'default';
  const isSynthese = entry.title === 'Synthèse Aladinn';

  return (
    <div className={`rounded-lg border p-4 relative group ${
      isFromChat
        ? 'bg-blue-50 border-blue-200' 
        : 'bg-white border-gray-200'
    }`}>
      <button
        onClick={() => setShowDeleteConfirm(true)}
        className="absolute top-2 right-2 p-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
        title="Supprimer cette note"
      >
        <X size={14} />
      </button>

      {showDeleteConfirm && (
        <div className="absolute inset-0 bg-white bg-opacity-95 rounded-lg flex items-center justify-center z-10 border-2 border-red-300">
          <div className="text-center p-4">
            <p className="text-sm font-medium text-gray-900 mb-3">
              Supprimer cette note ?
            </p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => {
                  onDelete(entry);
                  setShowDeleteConfirm(false);
                }}
                className="px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700"
              >
                Supprimer
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          {entry.icon && (
            <span className="text-xl">{entry.icon}</span>
          )}
          {entry.title && !isSynthese && (
            <div className="font-semibold text-gray-900 text-sm">
              {entry.title}
            </div>
          )}
          {hasCategory && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
              {entry.category}
            </span>
          )}
          {isFromChat && !isSynthese && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 flex items-center gap-1">
              <Sparkles size={10} />
              Chat
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {entry.timestamp && (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Clock size={12} />
              <span>{entry.timestamp}</span>
            </div>
          )}
          <div className="text-xs text-gray-400">
            #{index + 1}
          </div>
        </div>
      </div>

      <div className="text-gray-800 text-sm leading-relaxed space-y-2">
        {entry.content.map((line, lineIndex) => {
          let processedLine = line;
          
          // Traitement du formatage markdown
          processedLine = processedLine.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>');
          processedLine = processedLine.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em class="italic text-gray-600">$1</em>');
          
          // Détection des puces
          const isBullet = line.trim().match(/^[•\-]\s+(.+)/);
          
          // Détection des liens markdown [texte](url) et URLs simples
          const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/g;
          const urlRegex = /(https?:\/\/[^\s\)<]+)/g;
          
          const hasMarkdownLink = markdownLinkRegex.test(line);
          const hasUrl = urlRegex.test(line);
          
          if (hasMarkdownLink || hasUrl) {
            // Réinitialiser les regex
            markdownLinkRegex.lastIndex = 0;
            urlRegex.lastIndex = 0;
            
            return (
              <div key={lineIndex} className={isBullet ? "flex items-start gap-2 ml-2" : ""}>
                {isBullet && <span className="text-gray-400 mt-1">•</span>}
                <div className="flex-1">
                  {(() => {
                    const parts = [];
                    let workingLine = line;
                    
                    // Remplacer d'abord les liens markdown
                    if (hasMarkdownLink) {
                      workingLine = line.replace(markdownLinkRegex, (match, text, url) => {
                        return `__LINK_START__${url}__LINK_TEXT__${text}__LINK_END__`;
                      });
                    }
                    
                    // Découper par les marqueurs de liens
                    const segments = workingLine.split(/(__LINK_START__|__LINK_TEXT__|__LINK_END__)/);
                    
                    let i = 0;
                    while (i < segments.length) {
                      const segment = segments[i];
                      
                      if (segment === '__LINK_START__') {
                        const url = segments[i + 1];
                        const text = segments[i + 3];
                        parts.push(
                          <a
                            key={i}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            <ExternalLink size={12} />
                            <span>{text}</span>
                          </a>
                        );
                        i += 5; // Sauter __LINK_START__, url, __LINK_TEXT__, text, __LINK_END__
                      } else if (segment && !segment.startsWith('__LINK_')) {
                        // Vérifier s'il y a des URLs simples dans ce segment
                        const urlMatches = [...segment.matchAll(/(https?:\/\/[^\s\)<]+)/g)];
                        
                        if (urlMatches.length > 0) {
                          let lastIdx = 0;
                          urlMatches.forEach((match, idx) => {
                            // Texte avant l'URL
                            if (match.index > lastIdx) {
                              const beforeText = segment.substring(lastIdx, match.index);
                              if (beforeText) {
                                parts.push(<span key={`${i}-${idx}-before`} dangerouslySetInnerHTML={{ __html: beforeText }} />);
                              }
                            }
                            
                            // L'URL
                            parts.push(
                              <a
                                key={`${i}-${idx}-url`}
                                href={match[0]}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline break-all"
                              >
                                <ExternalLink size={12} className="flex-shrink-0" />
                                <span className="truncate max-w-[300px]">{match[0]}</span>
                              </a>
                            );
                            
                            lastIdx = match.index + match[0].length;
                          });
                          
                          // Texte après la dernière URL
                          if (lastIdx < segment.length) {
                            const afterText = segment.substring(lastIdx);
                            if (afterText) {
                              parts.push(<span key={`${i}-after`} dangerouslySetInnerHTML={{ __html: afterText }} />);
                            }
                          }
                        } else {
                          parts.push(<span key={i} dangerouslySetInnerHTML={{ __html: segment }} />);
                        }
                        i++;
                      } else {
                        i++;
                      }
                    }
                    
                    return parts;
                  })()}
                </div>
              </div>
            );
          }
          
          // Ligne sans lien
          if (isBullet) {
            return (
              <div key={lineIndex} className="flex items-start gap-2 ml-2">
                <span className="text-gray-400 mt-1">•</span>
                <span dangerouslySetInnerHTML={{ __html: processedLine.replace(/^[•\-]\s+/, '') }} />
              </div>
            );
          }
          
          return (
            <div key={lineIndex} dangerouslySetInnerHTML={{ __html: processedLine }} />
          );
        })}
      </div>
    </div>
  );
};

// Composant pour parser et afficher le contenu
const ContentDisplay = ({ value, rideau, onDeleteEntry, refreshKey }) => {
  const parsedEntries = useMemo(() => {
    if (!value) return [];

    console.log(`Parsing ${rideau.id}, refreshKey: ${refreshKey}`);

    const entries = [];
    
    const memoRegex = /###\s*([^\n]+)/g;
    const hasMemos = memoRegex.test(value);
    memoRegex.lastIndex = 0;
    
    if (hasMemos) {
      const parts = value.split(memoRegex);
      
      for (let i = 1; i < parts.length; i += 2) {
        const title = parts[i].replace(/🔥|🤖|⭐|📚|✅|✔️|📋|❓|⚠️|🎯|📢/g, '').trim();
        const content = parts[i + 1] || '';
        
        const dateMatch = content.match(/\*\*Date:\*\*\s*([^\n]+)/);
        const categoryMatch = content.match(/\*\*Catégorie:\*\*\s*([^\n]+)/);
        const sourceMatch = content.match(/\*\*Source:\*\*\s*([^\n]+)/);
        
        let mainContent = content;
        const contentMatch = content.match(/---\s*\n([\s\S]+?)\n---/);
        if (contentMatch) {
          mainContent = contentMatch[1];
        }
        
        const contentLines = mainContent
          .split('\n')
          .map(line => line.trim())
          .filter(line => line && !line.startsWith('**') && !line.startsWith('---'));
        
        entries.push({
          id: `memo_${i}_${Date.now()}_${refreshKey}`,
          title: title,
          timestamp: dateMatch ? dateMatch[1] : null,
          category: categoryMatch ? categoryMatch[1] : null,
          source: sourceMatch && sourceMatch[1].includes('Chat') ? 'chat' : 'manual',
          content: contentLines,
          rawContent: `### ${parts[i]}${content}`,
          startMarker: `### ${parts[i]}`
        });
      }
    } else {
      const sections = value.split('\n\n').filter(section => section.trim());
      
      sections.forEach((section, sectionIndex) => {
        const lines = section.split('\n').filter(line => line.trim());
        if (lines.length === 0) return;
        
        const firstLine = lines[0];
        
        const syntheseMatch = firstLine.match(/^([✅✔📮📚])\s*\[Synthèse Aladinn\]\s*(\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2}:\d{2})/);
        
        if (syntheseMatch) {
          const icon = syntheseMatch[1];
          const date = syntheseMatch[2];
          
          entries.push({
            id: `synthese_${sectionIndex}_${refreshKey}`,
            title: null,
            timestamp: date,
            category: null,
            source: 'chat',
            content: lines.slice(1),
            rawContent: section,
            icon: icon
          });
        } else {
          const dateMatch = firstLine.match(/(\d{2}\/\d{2}\/\d{4}[,\s]\d{2}:\d{2}:\d{2})/);
          
          entries.push({
            id: `legacy_${sectionIndex}_${refreshKey}`,
            title: null,
            timestamp: dateMatch ? dateMatch[1] : null,
            category: null,
            source: 'manual',
            content: dateMatch ? lines.slice(1) : lines,
            rawContent: section
          });
        }
      });
    }

    return entries.reverse();
  }, [value, rideau.id, refreshKey]);

  if (parsedEntries.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400 text-sm italic leading-relaxed">
          {rideau.placeholder}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {parsedEntries.map((entry, index) => (
        <NoteEntry 
          key={entry.id} 
          entry={entry} 
          index={index}
          onDelete={(entryToDelete) => onDeleteEntry(entryToDelete, rideau.id)}
        />
      ))}
    </div>
  );
};

// Composant pour une zone de saisie avec édition
const EditableTextArea = ({ rideau, value, onSave, isEditing, onStartEdit, onCancelEdit, onDeleteEntry, refreshKey }) => {
  const [localValue, setLocalValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setLocalValue(value || '');
  }, [value, rideau.id]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(rideau.id, localValue);
    } catch (error) {
      console.error('❌ Erreur sauvegarde:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setLocalValue(value || '');
    onCancelEdit();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleCancel();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSave();
    }
  };

  if (isEditing) {
    return (
      <div className="space-y-3">
        <textarea
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={rideau.placeholder}
          rows={12}
          className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm leading-relaxed font-mono"
          autoFocus
        />
        
        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-500">
            Ctrl/Cmd + Entrée pour sauvegarder • Échap pour annuler
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="flex items-center gap-1 px-4 py-2 bg-gray-500 text-white rounded-lg text-sm hover:bg-gray-600 disabled:opacity-50 transition-colors"
            >
              <X size={14} />
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isSaving ? (
                <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save size={14} />
              )}
              {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative group">
      <button
        onClick={onStartEdit}
        className="absolute -top-2 right-0 flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-600 rounded-lg text-xs hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 transition-all opacity-0 group-hover:opacity-100 shadow-sm z-10"
        title="Modifier les notes"
      >
        <Edit2 size={12} />
        <span>Modifier</span>
      </button>

      <ContentDisplay 
        value={value} 
        rideau={rideau} 
        onDeleteEntry={onDeleteEntry}
        refreshKey={refreshKey}
      />
    </div>
  );
};

// Composant pour un rideau AVEC indicateur de mise à jour
const RideauPanel = ({ 
  rideau, 
  value, 
  isExpanded, 
  onToggle, 
  onSave, 
  editingRideau, 
  onStartEdit, 
  onCancelEdit,
  onDeleteEntry,
  showUpdateIndicator,
  refreshKey
}) => {
  const IconComponent = rideau.icon;
  const hasContent = value && value.trim().length > 0;
  const isEditing = editingRideau === rideau.id;

  const entryStats = useMemo(() => {
    if (!hasContent) return { total: 0, fromChat: 0 };
    
    const memoCount = (value.match(/###\s*[^\n]+/g) || []).length;
    const chatCount = (value.match(/\*\*Source:\*\*\s*Conversation avec Aladinn/g) || []).length;
    const total = memoCount > 0 ? memoCount : value.split('\n\n').filter(s => s.trim()).length;
    
    return {
      total,
      fromChat: chatCount
    };
  }, [value, hasContent]);

  return (
    <div className={`border rounded-lg ${rideau.borderColor} ${rideau.bgColor} transition-all hover:shadow-sm relative`}>
      {/* Indicateur de mise à jour - Badge animé */}
      {showUpdateIndicator && (
        <div className="absolute -top-2 -right-2 z-10">
          <div className="relative">
            <span className="flex h-5 w-5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-5 w-5 bg-orange-500 border-2 border-white shadow-md"></span>
            </span>
          </div>
        </div>
      )}

      {/* En-tête du rideau */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${rideau.bgColor} border ${rideau.borderColor} relative`}>
            <IconComponent size={20} className={rideau.textColor} />
            {/* Indicateur alternatif : petit point sur l'icône */}
            {showUpdateIndicator && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-orange-500 rounded-full border border-white"></span>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">{rideau.title}</h3>
              {/* Badge "Nouveau" */}
              {showUpdateIndicator && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-orange-500 text-white rounded uppercase tracking-wide animate-pulse">
                  Nouveau
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600">{rideau.subtitle}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="text-right">
            {hasContent ? (
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-gray-700">
                  {entryStats.total > 1 ? `${entryStats.total} entrées` : '1 entrée'}
                </div>
                {entryStats.fromChat > 0 && (
                  <div className="text-xs text-blue-600 flex items-center gap-1 justify-end">
                    <Sparkles size={10} />
                    {entryStats.fromChat} du chat
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs text-gray-400">Vide</div>
            )}
          </div>
          
          <div className="text-gray-400">
            {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </div>
        </div>
      </div>

      {/* Contenu du rideau */}
      {isExpanded && (
        <div className="px-4 pb-4">
          <EditableTextArea
            rideau={rideau}
            value={value}
            isEditing={isEditing}
            onStartEdit={() => onStartEdit(rideau.id)}
            onCancelEdit={onCancelEdit}
            onSave={onSave}
            onDeleteEntry={onDeleteEntry}
            refreshKey={refreshKey}
          />
        </div>
      )}
    </div>
  );
};

// Header contextuel responsive
const ContextInfo = ({ 
  userObjective, 
  currentStep, 
  userProgress, 
  showMobileInfo, 
  setShowMobileInfo 
}) => (
  <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
    <div className="hidden md:block p-4 bg-gradient-to-r from-blue-50 to-indigo-50">
      <div className="flex items-center justify-between">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm flex-1">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <span className="font-medium">Objectif:</span>
            <span className="truncate flex-1">{userObjective || 'Non défini'}</span>
          </div>
          
          {currentStep && (
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-orange-600 flex-shrink-0" />
              <span className="font-medium">Étape:</span>
              <span className="truncate flex-1">{currentStep.titre}</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2 text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded-full ml-4">
          <Trophy className="h-4 w-4" />
          <span className="font-medium">{userProgress?.totalPoints || 0} pts</span>
        </div>
      </div>
    </div>

    <div className="md:hidden">
      <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-blue-600" />
            <span className="font-semibold text-gray-800 text-sm">Notes</span>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-full flex items-center gap-1">
              <Trophy className="h-3 w-3" />
              <span className="font-medium">{userProgress?.totalPoints || 0}</span>
            </div>
            
            <button
              onClick={() => setShowMobileInfo(!showMobileInfo)}
              className="p-1 hover:bg-white rounded-full transition-colors"
            >
              {showMobileInfo ? 
                <ChevronUp className="h-4 w-4 text-blue-600" /> : 
                <ChevronDown className="h-4 w-4 text-blue-600" />
              }
            </button>
          </div>
        </div>
      </div>
      
      {showMobileInfo && (
        <div className="p-3 bg-white border-b border-gray-200 space-y-3">
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <Target className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-gray-600 mb-1">Objectif</div>
                <div className="text-sm text-gray-800 leading-tight">{userObjective || 'Non défini'}</div>
              </div>
            </div>
            
            {currentStep && (
              <div className="flex items-start gap-2">
                <Zap className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-gray-600 mb-1">Étape courante</div>
                  <div className="text-sm text-gray-800 leading-tight">{currentStep.titre}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  </div>
);

// Composant principal NotesManager AVEC actualisation et badges
const NotesManager = ({ 
  actionPlan,
  currentStepId,
  getCurrentStep,
  competenceData,
  onSaveNote,
  userObjective,
  userProgress,
  notesTimestamp,
  updatedRideaux
}) => {
  const [stepNotes, setStepNotes] = useState({});
  const [expandedRideaux, setExpandedRideaux] = useState([]);
  const [editingRideau, setEditingRideau] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [showMobileInfo, setShowMobileInfo] = useState(false);
  const [viewedRideaux, setViewedRideaux] = useState(new Set());

  const currentStep = getCurrentStep();

  // Recharger les notes quand currentStepId ou notesTimestamp change
  useEffect(() => {
    console.log('Rechargement notes - stepId:', currentStepId, 'timestamp:', notesTimestamp);
    
    if (currentStepId && competenceData?.stepNotes?.[currentStepId]) {
      const stepData = competenceData.stepNotes[currentStepId];
      console.log('Notes chargées:', Object.keys(stepData).filter(k => !['stepId', 'lastUpdate', 'updatedAt'].includes(k)));
      setStepNotes(stepData);
      
      // Auto-expand les rideaux mis à jour
      if (updatedRideaux && updatedRideaux.size > 0) {
        setExpandedRideaux(prev => {
          const newExpanded = new Set([...prev, ...Array.from(updatedRideaux)]);
          return Array.from(newExpanded);
        });
      }
    } else {
      console.log('Aucune note pour cette étape');
      setStepNotes({});
    }
  }, [currentStepId, competenceData?.stepNotes, notesTimestamp, updatedRideaux]);

  // Marquer un rideau comme vu quand on l'expand
  const handleToggleRideau = useCallback((rideauId) => {
    setExpandedRideaux(prev => {
      const isCurrentlyExpanded = prev.includes(rideauId);
      if (isCurrentlyExpanded) {
        return prev.filter(id => id !== rideauId);
      } else {
        // Marquer comme vu
        setViewedRideaux(prev => new Set([...prev, rideauId]));
        return [...prev, rideauId];
      }
    });
  }, []);

  const handleDeleteEntry = useCallback(async (entryToDelete, rideauId) => {
    if (!currentStepId) return;

    console.log('Suppression note:', entryToDelete.id, 'dans', rideauId);

    try {
      const currentContent = stepNotes[rideauId] || '';
      let updatedContent = currentContent;
      
      if (entryToDelete.rawContent) {
        updatedContent = currentContent.replace(entryToDelete.rawContent, '');
        updatedContent = updatedContent.replace(/\n\n\n+/g, '\n\n').trim();
      }

      const updatedStepNotes = {
        ...stepNotes,
        [rideauId]: updatedContent,
        stepId: currentStepId,
        lastUpdate: rideauId,
        updatedAt: new Date().toISOString()
      };
      
      setStepNotes(updatedStepNotes);
      setSaveStatus('saving');

      const noteData = {
        sectionId: rideauId,
        content: updatedContent,
        rideauId,
        stepId: currentStepId,
        title: RIDEAU_CONFIG[rideauId].title,
        updatedAt: new Date().toISOString()
      };

      if (onSaveNote) {
        const success = await onSaveNote(noteData);
        if (success) {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 2000);
          console.log('Suppression sauvegardée');
        } else {
          throw new Error('Échec de la sauvegarde');
        }
      }
    } catch (error) {
      console.error('Erreur suppression note:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [currentStepId, stepNotes, onSaveNote]);

  const handleStartEdit = useCallback((rideauId) => {
    if (editingRideau && editingRideau !== rideauId) {
      setEditingRideau(null);
      setTimeout(() => setEditingRideau(rideauId), 100);
    } else {
      setEditingRideau(rideauId);
    }
  }, [editingRideau]);

  const handleCancelEdit = useCallback(() => {
    setEditingRideau(null);
    setSaveStatus('idle');
  }, []);

  const handleSave = useCallback(async (rideauId, content) => {
    if (!currentStepId) {
      console.error('Aucune étape courante pour sauvegarder');
      return;
    }

    setSaveStatus('saving');

    const updatedStepNotes = {
      ...stepNotes,
      [rideauId]: content,
      stepId: currentStepId,
      lastUpdate: rideauId,
      updatedAt: new Date().toISOString()
    };

    setStepNotes(updatedStepNotes);
    setEditingRideau(null);

    try {
      const noteData = {
        sectionId: rideauId,
        content,
        rideauId,
        stepId: currentStepId,
        title: RIDEAU_CONFIG[rideauId].title,
        updatedAt: new Date().toISOString()
      };

      if (onSaveNote) {
        const success = await onSaveNote(noteData);
        if (success) {
          setLastSaved(new Date());
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 2000);
        } else {
          throw new Error('Échec de la sauvegarde');
        }
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
      
      setStepNotes(prev => {
        const rollback = { ...prev };
        delete rollback[rideauId];
        return rollback;
      });
    }
  }, [currentStepId, stepNotes, onSaveNote]);

  return (
    <div className="h-full flex flex-col bg-white">
      <ContextInfo 
        userObjective={userObjective}
        currentStep={currentStep}
        userProgress={userProgress}
        showMobileInfo={showMobileInfo}
        setShowMobileInfo={setShowMobileInfo}
      />

      <div className="border-b border-gray-200 px-4 py-2 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {saveStatus !== 'idle' && (
              <div className="flex items-center gap-1 text-xs">
                {saveStatus === 'saving' && (
                  <>
                    <div className="w-3 h-3 border border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-blue-600">Sauvegarde...</span>
                  </>
                )}
                {saveStatus === 'saved' && (
                  <>
                    <CheckCircle size={12} className="text-green-600" />
                    <span className="text-green-600">Sauvegardé</span>
                  </>
                )}
                {saveStatus === 'error' && (
                  <>
                    <AlertCircle size={12} className="text-red-600" />
                    <span className="text-red-600">Erreur</span>
                  </>
                )}
              </div>
            )}
            
            {lastSaved && saveStatus === 'idle' && (
              <div className="text-xs text-gray-500 flex items-center gap-1">
                <Clock size={12} />
                {lastSaved.toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-4">
        {currentStepId ? (
          Object.entries(RIDEAU_CONFIG).map(([rideauId, rideau]) => {
            const wasUpdated = updatedRideaux?.has(rideauId);
            const wasViewed = viewedRideaux.has(rideauId);
            const showBadge = wasUpdated && !wasViewed;

            return (
              <RideauPanel
                key={rideauId}
                rideau={rideau}
                value={stepNotes[rideauId]}
                isExpanded={expandedRideaux.includes(rideauId)}
                onToggle={() => handleToggleRideau(rideauId)}
                onSave={handleSave}
                editingRideau={editingRideau}
                onStartEdit={handleStartEdit}
                onCancelEdit={handleCancelEdit}
                onDeleteEntry={handleDeleteEntry}
                showUpdateIndicator={showBadge}
                refreshKey={notesTimestamp}
              />
            );
          })
        ) : (
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Sélectionnez une étape
            </h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Choisissez une étape de votre plan d'action pour commencer à documenter 
              vos décisions, questions et voir les recommandations d'Aladinn
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesManager;